import styled, { css } from 'styled-components'
import { tabletBreakpoint, desktopBreakpoint, wideScreenBreakpoint } from 'react-components'
import CloseIcon from '../../../../assets/icons/close.svg'
import ChevronDownIcon from '../../../../assets/icons/chevron-down.svg'
import { LAYOUT } from '../../../../constants/search-result-listing'
import { zIndexScale } from '../../../../utils/style-utils'

export const TrayViewport = styled.div<{ $visible: boolean }>(
    ({ theme, $visible }) => css`
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: ${zIndexScale.largeTags.xxxSmall};
        display: flex;
        justify-content: center;
        padding-bottom: ${theme.spacing.medium};
        pointer-events: none;
        transform: translateY(${$visible ? '0' : '150%'});
        visibility: ${$visible ? 'visible' : 'hidden'};
        transition:
            transform 0.3s ease-in-out,
            visibility 0s linear ${$visible ? '0s' : '0.3s'};

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding-bottom: ${theme.spacing.large};
        }
    `,
)

export const TrayAligner = styled.div(
    ({ theme }) => css`
        display: flex;
        width: 100%;
        max-width: ${wideScreenBreakpoint.min}px;
        margin: 0 auto;
        padding: 0 ${theme.spacing.medium};
        gap: ${theme.spacing.large};

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: 0 ${theme.spacing.large};
        }
    `,
)

export const SidebarSpacer = styled.div`
    display: none;
    flex-shrink: 0;

    @media only screen and (min-width: ${desktopBreakpoint.min}px) {
        display: block;
        width: ${LAYOUT.SMALL_SIDEBAR_WIDTH}rem;
    }

    @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
        width: ${LAYOUT.SIDEBAR_WIDTH}rem;
    }
`

export const OuterBar = styled.div(
    ({ theme }) => css`
        pointer-events: auto;
        box-sizing: border-box;
        flex: 1;
        min-width: 0;
        background-color: ${theme.colors.tfwBlack99};
        border-radius: ${theme.typography.border.radius.xxLarge};
        padding: ${theme.spacing.xSmall} ${theme.spacing.xxxSmall} ${theme.spacing.xxxSmall} ${theme.spacing.xxxSmall};
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing.small};
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            margin: 0 3.31rem;
        }
    `,
)

export const HeadingRow = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing.xSmall};
    `,
)

export const Heading = styled.p(
    ({ theme }) => css`
        margin: 0;
        padding: ${theme.spacing.xxSmall} ${theme.spacing.small};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.375rem;
        color: ${theme.colors.white};
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `,
)

export const HeaderControls = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing.xxSmall};
        flex-shrink: 0;
    `,
)

export const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;

    &:hover {
        background-color: rgba(255, 255, 255, 0.12);
    }
`

export const CollapseChevron = styled(ChevronDownIcon)(
    ({ theme }) => css`
        width: 1.25rem;
        height: 1.25rem;
        path {
            fill: ${theme.colors.white};
        }
    `,
)

export const CollapsedBar = styled.button(
    ({ theme }) => css`
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing.xSmall};
        padding: 12px 14px 18px 14px;
        border: none;
        background: transparent;
        cursor: pointer;
    `,
)

export const CollapsedLabel = styled.span(
    ({ theme }) => css`
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.375rem;
        color: ${theme.colors.white};
    `,
)

export const ExpandChevron = styled(ChevronDownIcon)(
    ({ theme }) => css`
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
        transform: rotate(180deg);
        path {
            fill: ${theme.colors.white};
        }
    `,
)

export const WhiteCard = styled.div(
    ({ theme }) => css`
        background-color: ${theme.colors.white};
        border-radius: ${theme.typography.border.radius.xLarge};
        padding: ${theme.spacing.medium};
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing.medium};

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            flex-direction: row;
            align-items: center;
        }
    `,
)

export const SlotsRow = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: flex-start;
        gap: ${theme.spacing.small};
        flex: 1;
        min-width: 0;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            gap: ${theme.spacing.medium};
        }
    `,
)

export const FilledSlot = styled.div`
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    max-width: 3.5rem;
    aspect-ratio: 1;

    @media only screen and (min-width: ${tabletBreakpoint.min}px) {
        max-width: 4rem;
    }
`

export const SlotImage = styled.img(
    ({ theme }) => css`
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: ${theme.typography.border.radius.medium};
        display: block;
    `,
)

export const RemoveButton = styled.button(
    ({ theme }) => css`
        position: absolute;
        top: -0.25rem;
        right: -0.25rem;
        width: 1.25rem;
        height: 1.25rem;
        padding: 0;
        border: none;
        border-radius: 50%;
        background-color: ${theme.colors.tfwBlack99};
        color: ${theme.colors.white};
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    `,
)

export const StyledCloseIcon = styled(CloseIcon)(
    ({ theme }) => css`
        width: 0.5rem;
        height: 1rem;

        path {
            fill: ${theme.colors.white};
        }
    `,
)

export const EmptySlot = styled.div(
    ({ theme }) => css`
        flex: 1 1 0;
        min-width: 0;
        max-width: 3.5rem;
        aspect-ratio: 1;
        background-color: ${theme.colors.tfwGrey};
        border: ${theme.typography.border.width.xxxThin} dashed ${theme.colors.tfwDefaultInputBorder};
        border-radius: ${theme.typography.border.radius.medium};

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            max-width: 4rem;
        }
    `,
)

export const Actions = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: stretch;
        gap: ${theme.spacing.xSmall};
        width: 100%;

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            align-items: center;
            gap: ${theme.spacing.small};
            width: auto;
            margin-left: auto;
        }
    `,
)

export const CompareWrap = styled.div`
    position: relative;
    display: flex;
    flex: 0 0 auto;
`

export const CompareTooltip = styled.span(
    ({ theme }) => css`
        position: absolute;
        bottom: calc(100% + 0.625rem);
        left: 50%;
        transform: translateX(-50%);
        z-index: ${zIndexScale.largeTags.medium};
        width: 13rem;
        max-width: 80vw;
        padding: 0.625rem ${theme.spacing.small};
        border-radius: ${theme.typography.border.radius.medium};
        background-color: ${theme.colors.tfwBlack99};
        color: ${theme.colors.white};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.xSmall};
        font-weight: ${theme.typography.font.lato.weight.regular};
        line-height: 1.125rem;
        text-align: center;
        white-space: normal;
        opacity: 0;
        visibility: hidden;
        transition:
            opacity 0.15s ease-in-out,
            visibility 0.15s ease-in-out;

        &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-width: 0.375rem;
            border-style: solid;
            border-color: ${theme.colors.tfwBlack99} transparent transparent transparent;
        }

        ${CompareWrap}:hover & {
            opacity: 1;
            visibility: visible;
        }
    `,
)

export const CompareButton = styled.button(
    ({ theme }) => css`
        flex: 0 0 auto;
        padding: ${theme.spacing.xSmall} ${theme.spacing.small};
        border: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwBlack99};
        border-radius: ${theme.typography.border.radius.medium};
        background-color: ${theme.colors.white};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.25rem;
        color: ${theme.colors.tfwBlack99};
        cursor: pointer;
        text-align: center;

        &:disabled {
            border-color: ${theme.colors.tfwDefaultInputBorder};
            color: ${theme.colors.tfwDefaultInputBorder};
            cursor: not-allowed;
        }

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            min-width: 4.5rem;
            padding: 0.625rem 1.5rem;
            white-space: nowrap;
        }
    `,
)

export const SendButton = styled.button(
    ({ theme }) => css`
        flex: 1;
        padding: ${theme.spacing.xSmall} ${theme.spacing.small};
        border: none;
        border-radius: ${theme.typography.border.radius.medium};
        background-color: ${theme.colors.tfwYellow};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.25rem;
        color: ${theme.colors.tfwBlack99};
        cursor: pointer;
        text-align: center;

        &:hover {
            background-color: ${theme.colors.tfwYellowHover};
        }

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            flex: 0 0 auto;
            min-width: 4.5rem;
            padding: 0.625rem 1.5rem;
            white-space: nowrap;
        }
    `,
)
